/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Avatar } from 'xland-ui';
import Portal from 'components/Portal';
import { useRect } from 'utils/customHooks';
import { getStartupVerifiedIcon, normalizeText } from 'utils/functions';
import { connect } from 'react-redux';
import { PlusSquare, X } from 'lucide-react';
import { getFunds } from 'components/FundSearcher/modules/actions';
import { getInvestmentAssets } from 'modules/investmentAssets/repository';
import { fetchStartups } from './modules/actions';
import './styles.scss';

const SEARCH_DEBOUNCE_MS = 750;
const MIN_SEARCH_LENGTH = 2;
const PAGE_SIZE = 25;

function StartupSearcher({
  className = '',
  literals,
  value = '',
  onChange,
  preText = '',
  postText = '',
  placeholder = '',
  isRequired = false,
  isDisabled = false,
  error = null,
  type,
}) {
  const [search, setSearch] = useState({ value, normalized: value });
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const [bbox, inputRef, visible] = useRect(showOptions || loading);
  const searchTimeout = useRef(null);

  const fetchInvestmentAssets = async () => {
    switch (type) {
      case 'startup':
      case 'company':
        return fetchStartups(0, PAGE_SIZE, search.normalized, {
          verified: ['team', 'full'],
        });
      case 'fund': {
        const response = await getFunds(0, PAGE_SIZE, search.normalized);
        return response.items;
      }
      default: {
        const response = await getInvestmentAssets({ search: search.normalized });
        return response.items;
      }
    }
  };

  useEffect(() => {
    if (search.value.length >= MIN_SEARCH_LENGTH) {
      searchTimeout.current = setTimeout(async () => {
        setLoading(true);
        try {
          const results = await fetchInvestmentAssets();
          setOptions(results);
          setShowOptions(true);
        } catch (err) {
          console.error('Error fetching investment assets:', err);
        } finally {
          setLoading(false);
        }
      }, SEARCH_DEBOUNCE_MS);
    }
    return () => clearTimeout(searchTimeout.current);
  }, [search]);

  const selectValue = (id) => {
    const foundValue = id ? options.find(elem => elem.id === id || elem.externalId === id) : null;
    onChange(foundValue || (id ? { id: 'other', name: search.value } : null));
    setSearch({ value: '', normalized: '' });
    setShowOptions(false);
  };

  useEffect(() => {
    if (type) {
      setSearch({ value: '', normalized: '' });
      selectValue(null);
    }
  }, [type]);

  const renderRow = (item) => {
    if (item?.id === 'other') {
      return (
        <>
          <PlusSquare strokeWidth={1.5} size={30} className='ms-1' />
          <div className='ms-3'>{item?.name || literals.noneAbove}</div>
        </>
      );
    }

    return (
      <>
        <Avatar size={40} src={item.logo?.path} title={item.name} />
        <div className='ms-3'>
          <div>{item.name}</div>
          <div className='fc-secondary fs-sm'>{item.url}</div>
        </div>
        {getStartupVerifiedIcon(item.verified)}
      </>
    );
  };

  const renderOptions = () => ([
    ...options.map(elem => (
      <div
        key={elem?.id || elem?.externalId}
        className='input-select-item'
        onClick={() => selectValue(elem.id || elem.externalId)}
      >
        {renderRow(elem)}
      </div>
    )),
    <div
      key='other'
      className='input-select-item'
      onClick={() => selectValue('other')}
    >
      {renderRow({ id: 'other' })}
    </div>,
  ]);

  const styles = {
    visible,
    ...(bbox && {
      top: bbox.bottom,
      left: bbox.left,
      width: bbox.width,
    }),
  };

  return (
    <div className={`input-select-wrapper value-inside startup-searcher ${className} ${isDisabled ? 'disabled' : ''}`}>
      {preText && (
        <div className={`pre_text ${error ? 'text_error' : ''}`}>
          {preText}
          {isRequired && <span className='is_required'>*</span>}
        </div>
      )}
      <div
        className={`input-select-select ${error ? 'input_error' : ''} ${showOptions ? 'no-border' : ''}`}
        ref={inputRef}
        onClick={() => inputRef.current.focus()}
      >
        <div className='selected-item'>
          {!value ? (
            <input
              className='input-select-text'
              placeholder={placeholder}
              type='text'
              value={search.value}
              onClick={e => e.stopPropagation()}
              onChange={e => setSearch({
                value: e.target.value,
                normalized: normalizeText(e.target.value),
              })}
              disabled={isDisabled}
            />
          ) : (
            <div className='startup-selected'>
              <div className='d-flex align-items-center flex-grow-1'>
                {renderRow(value)}
              </div>
              <div style={{ width: '20px', marginLeft: '10px', cursor: 'pointer' }}>
                <X size={20} onClick={() => !isDisabled && selectValue(null)} />
              </div>
            </div>
          )}
        </div>
      </div>
      {(showOptions || loading) && (
        <Portal styles={styles} onClose={() => setShowOptions(false)}>
          <div className='input-select-item-options simple-scrollbar'>
            {loading ? (
              <div key='loading' className='input-select-item'>
                {literals.common.loading}
              </div>
            ) : renderOptions()}
          </div>
        </Portal>
      )}
      {postText && <div className='post_text' dangerouslySetInnerHTML={{ __html: postText }} />}
    </div>
  );
}

StartupSearcher.propTypes = {
  literals: PropTypes.object.isRequired,
  preText: PropTypes.string,
  postText: PropTypes.string,
  isRequired: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  placeholder: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
};

const mapStateToProps = (state) => ({
  literals: {
    ...state.i18n.literals.input,
    common: state.i18n.literals.common,
  },
});

export default connect(mapStateToProps, {})(StartupSearcher);
